import {makeAutoObservable, observable} from 'mobx'
import {ObjUtil} from 'yougile-util'
import {
  AutoDoneTaskWhenMoveToCol,
  AutoTaskCover,
  DataObjectId,
  Hub,
  HubProperties,
  MoveTaskToColWhenDone
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'

interface State {
  boardId: DataObjectId | undefined
  showTimeTaskInColumn?: boolean
  showCountMsgsInTask?: boolean
  showZeroCountMsgsInTask?: boolean
  autoCompleteTasks?: boolean
  counterInColumns?: boolean
  moveTaskToColWhenDone?: MoveTaskToColWhenDone
  autoDoneTaskWhenMoveToCol?: AutoDoneTaskWhenMoveToCol
  autoTaskCover?: AutoTaskCover
  showIdOnTaskCard?: boolean
}

const isEqual = (a: unknown, b: unknown): boolean => {
  return ObjUtil.deepEqual(a, b, false)
}

export class GeneralBoardSettingsPopupStore {
  state: State = {
    boardId: undefined,
    showTimeTaskInColumn: false,
    showCountMsgsInTask: false,
    showZeroCountMsgsInTask: true,
    moveTaskToColWhenDone: undefined,
    autoDoneTaskWhenMoveToCol: undefined,
    autoTaskCover: undefined
  }

  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  setBoard(boardId: DataObjectId | undefined): void {
    this.state.boardId = boardId
  }

  close(): void {
    this.setBoard(undefined)
  }

  getBoard(): Hub {
    return Sync.getCurrentSandbox().getHub(this.state.boardId)
  }

  getBoardWithInitedSettings(): Hub {
    const board = this.getBoard()
    board.data ??= {}
    board.data.settings ??= {}
    return board
  }

  loadActualData(): void {
    const board = this.getBoard()
    const settings = board?.data?.settings

    const showTimeTaskInColumn = settings?.showTimeTaskInColumn
    if (showTimeTaskInColumn !== this.state.showTimeTaskInColumn) {
      this.setShowTimeTaskInColumn(!!showTimeTaskInColumn)
    }

    const showCountMsgsInTask =
      !!board?.properties?.[HubProperties.showMessageStatsInBoard]
    if (showCountMsgsInTask !== this.state.showCountMsgsInTask) {
      this.setShowCountMsgsInTask(showCountMsgsInTask)
    }

    const showZeroCountMsgsInTask =
      !!board?.properties?.[HubProperties.showZeroMessageStatsInBoard]
    if (showZeroCountMsgsInTask !== this.state.showZeroCountMsgsInTask) {
      this.setShowZeroCountMsgsInTask(showZeroCountMsgsInTask)
    }

    const autoCompleteTasks = settings?.autoCompleteTasks
    if (autoCompleteTasks !== this.state.autoCompleteTasks) {
      this.setAutoCompleteTasks(!!autoCompleteTasks)
    }

    const counterInColumns = settings?.counterInColumns
    if (counterInColumns !== this.state.counterInColumns) {
      this.setCounterInColumns(!!counterInColumns)
    }

    const moveTaskToColWhenDone = settings?.moveTaskToColWhenDone
    if (
      moveTaskToColWhenDone &&
      !isEqual(moveTaskToColWhenDone, this.state.moveTaskToColWhenDone)
    ) {
      this.setMoveTaskToColWhenDone(moveTaskToColWhenDone)
    }

    const autoDoneTaskWhenMoveToCol = settings?.autoDoneTaskWhenMoveToCol
    if (
      autoDoneTaskWhenMoveToCol &&
      !isEqual(autoDoneTaskWhenMoveToCol, this.state.autoDoneTaskWhenMoveToCol)
    ) {
      this.setAutoDoneTaskWhenMoveToCol(autoDoneTaskWhenMoveToCol)
    }

    const autoTaskCover = settings?.autoTaskCover
    if (autoTaskCover && !isEqual(autoTaskCover, this.state.autoTaskCover)) {
      this.setAutoTaskCover(autoTaskCover)
    }

    const showIdOnTaskCard = !!settings?.showIdOnTaskCard
    if (!showIdOnTaskCard !== this.state.showIdOnTaskCard) {
      this.setShowIdOnTaskCard(showIdOnTaskCard)
    }
  }

  //

  onToggleShowTimeTaskInColumn(): void {
    const currentValue = this.state.showTimeTaskInColumn
    const newValue = !currentValue

    this.setShowTimeTaskInColumn(newValue)
    this.commitShowTimeTaskInColumn(newValue)
  }

  setShowTimeTaskInColumn(value: boolean): void {
    this.state.showTimeTaskInColumn = value
  }

  commitShowTimeTaskInColumn(value: boolean): void {
    const board = this.getBoardWithInitedSettings()

    board.data ??= {}
    board.data.settings ??= {}

    if (board.data.settings.showTimeTaskInColumn !== value) {
      board.data.settings.showTimeTaskInColumn = value
      board.commit()
    }
  }

  //

  onToggleShowCountMsgsInTask(): void {
    const newValue = !this.state.showCountMsgsInTask

    this.setShowCountMsgsInTask(newValue)
    this.commitShowCountMsgs(newValue)
  }

  onToggleShowZeroCountMsgsInTask(): void {
    const newValue = !this.state.showZeroCountMsgsInTask

    this.setShowZeroCountMsgsInTask(newValue)
    this.commitShowZeroCountMsgs(newValue)
  }

  setShowCountMsgsInTask(value: boolean): void {
    this.state.showCountMsgsInTask = value

    if (value && !this.state.showZeroCountMsgsInTask) {
      this.onToggleShowZeroCountMsgsInTask()
    }
  }

  setShowZeroCountMsgsInTask(value: boolean): void {
    this.state.showZeroCountMsgsInTask = value
  }

  commitShowCountMsgs(value: boolean): void {
    const board = this.getBoard()
    if (board.properties[HubProperties.showMessageStatsInBoard] !== value) {
      board.properties ??= {}
      board.properties[HubProperties.showMessageStatsInBoard] = value
      board.commit()
    }
  }

  commitShowZeroCountMsgs(value: boolean): void {
    const board = this.getBoard()
    if (board.properties[HubProperties.showZeroMessageStatsInBoard] !== value) {
      board.properties ??= {}
      board.properties[HubProperties.showZeroMessageStatsInBoard] = value
      board.commit()
    }
  }

  //

  onToggleShowIdOnTaskCard(): void {
    const currentValue = this.state.showIdOnTaskCard
    const newValue = !currentValue

    if (newValue) {
      void Tracking.send(TrackingEvent.boardSettingsShowIdOnTaskCardOn)
    } else {
      void Tracking.send(TrackingEvent.boardSettingsShowIdOnTaskCardOff)
    }

    this.setShowIdOnTaskCard(newValue)

    const board = this.getBoard()
    if (board.data?.settings?.showIdOnTaskCard !== newValue) {
      board.data ??= {}
      board.data.settings ??= {}
      board.data.settings.showIdOnTaskCard = newValue
      board.commit()
    }
  }

  setShowIdOnTaskCard(value: boolean): void {
    this.state.showIdOnTaskCard = value
  }

  //

  onToggleAutoCompleteTasks(): void {
    const currentValue = this.state.autoCompleteTasks
    const newValue = !currentValue

    if (newValue) {
      void Tracking.send(TrackingEvent.boardSettingsAutoCompleteOn)
    } else {
      void Tracking.send(TrackingEvent.boardSettingsAutoCompleteOff)
    }

    this.setAutoCompleteTasks(newValue)
    this.commitAutoCompleteTasks(newValue)
  }

  setAutoCompleteTasks(value: boolean): void {
    this.state.autoCompleteTasks = value
  }

  commitAutoCompleteTasks(value: boolean): void {
    const board = this.getBoard()
    if (board.data?.settings?.autoCompleteTasks !== value) {
      board.data ??= {}
      board.data.settings ??= {}
      board.data.settings.autoCompleteTasks = value
      board.commit()
    }
  }

  //11

  onToggleCounterInColumns(): void {
    const currentValue = this.state.counterInColumns
    const newValue = !currentValue

    if (newValue) {
      void Tracking.send(TrackingEvent.boardSettingsShowCounterInColumnOn)
    } else {
      void Tracking.send(TrackingEvent.boardSettingsShowCounterInColumnOff)
    }

    this.setCounterInColumns(newValue)
    this.commitCounterInColumns(newValue)
  }

  setCounterInColumns(value: boolean): void {
    this.state.counterInColumns = value
  }

  commitCounterInColumns(value: boolean): void {
    const board = this.getBoard()
    if (board.data?.settings?.counterInColumns !== value) {
      board.data ??= {}
      board.data.settings ??= {}
      board.data.settings.counterInColumns = value
      board.commit()
    }
  }

  //

  onToggleMoveTaskToColWhenDone(): void {
    const currentValue = this.state.moveTaskToColWhenDone
    const newChecked = !currentValue?.checked
    const newValue = {checked: newChecked}
    this.setMoveTaskToColWhenDone(newValue)
    this.commitMoveTaskToColWhenDone(newValue)
  }

  setMoveTaskToColWhenDone(value: MoveTaskToColWhenDone): void {
    this.state.moveTaskToColWhenDone = value
  }

  commitMoveTaskToColWhenDone(value: MoveTaskToColWhenDone): void {
    const board = this.getBoardWithInitedSettings()

    board.data ??= {}
    board.data.settings ??= {}
    board.data.settings.moveTaskToColWhenDone = value
    board.commit()
  }

  //

  onToggleAutoDoneTaskWhenMoveToCol(): void {
    const currentValue = this.state.autoDoneTaskWhenMoveToCol
    const newChecked = !currentValue?.checked

    const newValue = {checked: newChecked}
    this.setAutoDoneTaskWhenMoveToCol(newValue)
    this.commitAutoDoneTaskWhenMoveToCol(newValue)
  }

  setAutoDoneTaskWhenMoveToCol(value: AutoDoneTaskWhenMoveToCol): void {
    this.state.autoDoneTaskWhenMoveToCol = value
  }

  commitAutoDoneTaskWhenMoveToCol(value: AutoDoneTaskWhenMoveToCol): void {
    const board = this.getBoardWithInitedSettings()

    board.data ??= {}
    board.data.settings ??= {}
    board.data.settings.autoDoneTaskWhenMoveToCol = value
    board.commit()
  }

  //

  onToggleAutoTaskCover(): void {
    const currentValue = this.state.autoTaskCover
    const newChecked = !currentValue?.checked

    const newValue = {checked: newChecked}
    this.setAutoTaskCover(newValue)
    this.commitAutoTaskCover(newValue)
  }

  setAutoTaskCover(value: AutoTaskCover): void {
    this.state.autoTaskCover = value
  }

  commitAutoTaskCover(value: AutoTaskCover): void {
    const board = this.getBoardWithInitedSettings()

    const prevValue = board.data.settings?.autoTaskCover ?? {checked: false}
    board.data.settings = {...board.data.settings, autoTaskCover: value}
    board.commit()

    this.trackAutoTaskCover(prevValue, value)
  }

  private trackAutoTaskCover(prev: AutoTaskCover, curr: AutoTaskCover): void {
    if (!prev.checked && curr.checked) {
      void Tracking.send(TrackingEvent.boardSettingsEnableAutoTaskCover)
      void Tracking.send(TrackingEvent.boardSettingsSetAutoTaskCoverColumns, {
        total: 'all'
      })
    } else if (prev.checked && !curr.checked) {
      void Tracking.send(TrackingEvent.boardSettingsDisableAutoTaskCover)
    } else if (prev.checked && curr.checked) {
      void Tracking.send(TrackingEvent.boardSettingsSetAutoTaskCoverColumns, {
        total: curr.values?.length ? 'some' : 'all'
      })
    }
  }
}
